import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataBody, BodyVolume } from '../../types';
import * as api from '../../services/api';
import BuildDataBodyForm, { BuildFormState, emptyBuildForm, createFromBuildForm } from '../DataBodyBuilder/BuildDataBodyForm';
import DataBodySettingsModal from './DataBodySettingsModal';

// Dashboard view mode — persisted to localStorage so the user's preference survives reloads.
type ViewMode = 'grid' | 'list';
const VIEW_MODE_KEY = 'databodies.dashboard.viewMode';

// Active / Archived tab. Persisted so a user who just archived something
// and reloads doesn't get bumped back to "Active" and wonder where it went.
type DashboardTab = 'active' | 'archived';
const ACTIVE_TAB_KEY = 'databodies.dashboard.activeTab';

// -------------------------------------------------------------------
// Sorting
// -------------------------------------------------------------------
// One sort state drives BOTH views. In list view the column headers are
// the control; in grid view a dropdown in the toolbar is. They read and
// write the same state, so switching views never silently reorders the
// cards under you. Persisted for the same reason the view mode is: a sort
// you chose is a decision, and losing it on reload is annoying.
const SORT_KEY = 'databodies.dashboard.sort';

type SortKey = 'source' | 'name' | 'status' | 'today' | 'sevenDay' | 'gross' | 'deliveryRate' | 'createdAt' | 'updatedAt';
type SortDir = 'asc' | 'desc';
type SortState = { key: SortKey; dir: SortDir };

const DEFAULT_SORT: SortState = { key: 'today', dir: 'desc' };

// Column definitions — the single source of truth for the hierarchy Scott
// specified: Source, Name, Status, then volume over Today / 7 Days / Gross,
// then Delivery Rate. Both views read this list, which is what keeps them
// from drifting apart.
const COLUMNS: Array<{ key: SortKey; label: string; numeric: boolean }> = [
  { key: 'source', label: 'Source', numeric: false },
  { key: 'name', label: 'Name', numeric: false },
  { key: 'status', label: 'Status', numeric: false },
  { key: 'today', label: 'Today', numeric: true },
  { key: 'sevenDay', label: '7 Days', numeric: true },
  { key: 'gross', label: 'Gross', numeric: true },
  { key: 'deliveryRate', label: 'Delivery Rate', numeric: true },
  // Timestamps sort numerically (epoch ms) so they default to newest-first.
  { key: 'createdAt', label: 'Created', numeric: true },
  { key: 'updatedAt', label: 'Updated', numeric: true },
];

/** Numeric columns default to biggest-first; text columns to A–Z. */
function defaultDirFor(key: SortKey): SortDir {
  const col = COLUMNS.find(c => c.key === key);
  return col?.numeric ? 'desc' : 'asc';
}

function readStoredSort(): SortState {
  try {
    const raw = localStorage.getItem(SORT_KEY);
    if (!raw) {return DEFAULT_SORT;}
    const parsed = JSON.parse(raw);
    // Validate rather than trust — a stale key from an earlier column set
    // would otherwise sort by a field that no longer exists and silently
    // render everything in insertion order.
    if (COLUMNS.some(c => c.key === parsed?.key) && (parsed?.dir === 'asc' || parsed?.dir === 'desc')) {
      return { key: parsed.key, dir: parsed.dir };
    }
  } catch {
    /* ignore malformed/unavailable storage */
  }
  return DEFAULT_SORT;
}

// -------------------------------------------------------------------
// Stat number formatting
// -------------------------------------------------------------------
// Production data bodies will see millions of leads. Rendering
// "1,234,567" in a tight stat card overflows the cell width and clips
// onto the next card (Scott's screenshot showed "73954" already
// breaking out of its container).
//
// Solution: compact notation. Intl.NumberFormat with notation:'compact'
// gives us "1.2M" for >= 1,000,000 and "1.2K" for 1,000 to 999,999, and
// renders small numbers (< 1,000) as-is. Tooltip carries the exact
// value for users who want the precise count.
//
// Examples: 0 → "0", 489 → "489", 1234 → "1.2K", 12500 → "12.5K",
// 757437 → "757K", 1234567 → "1.2M", 12345678 → "12M".
const COMPACT_FORMATTER = new Intl.NumberFormat('en', {
  notation: 'compact',
  maximumFractionDigits: 1,
});
function fmtStat(n: number | undefined | null): string {
  if (n == null) {return '0';}
  return COMPACT_FORMATTER.format(n);
}
function fmtStatExact(n: number | undefined | null): string {
  if (n == null) {return '0';}
  return n.toLocaleString();
}

/**
 * Delivery rate display.
 *
 * The value is a RATIO, not a percentage: 1.98 means 198%. Over 100% is the
 * normal case, not an error — one accepted lead delivered to three
 * destinations is three leg deliveries against one accepted lead. Never
 * clamp it; the whole point of the metric is that it can be well above or
 * well below 100, and both directions mean something.
 *
 * null = nothing came in today, so there is no rate. Renders as an em dash,
 * never as "0%", which would read as a total delivery failure.
 */
function fmtRate(rate: number | null | undefined): string {
  if (rate == null) {return '—';}
  const pct = rate * 100;
  // Sub-1% would otherwise round to "0%" and look like the null case.
  if (pct > 0 && pct < 1) {return '<1%';}
  return `${Math.round(pct)}%`;
}

/**
 * Colour the rate by what it says about delivery health:
 *   >= 100%  green   — everything that came in went out (often to several legs)
 *   >= 60%   amber   — a meaningful share isn't reaching a destination
 *   <  60%   red     — data is being accepted and then going nowhere
 * This is the failure mode the old "accept rate" column couldn't see: leads
 * accepted cleanly, then never delivered.
 */
function rateClassName(rate: number | null | undefined): string {
  if (rate == null) {return 'rate-none';}
  if (rate >= 1) {return 'rate-good';}
  if (rate >= 0.6) {return 'rate-warn';}
  return 'rate-bad';
}

/**
 * Card footer timestamps. Short form on the card ("Oct 27, 2026, 3:04 PM"),
 * full locale string in the tooltip. Invalid/missing dates render as an em
 * dash rather than "Invalid Date".
 */
const CARD_DATE_FORMATTER = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short',
});
function fmtCardDate(iso: string | null | undefined): string {
  if (!iso) {return '—';}
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : CARD_DATE_FORMATTER.format(d);
}
/** List cells are narrow: date only ("Oct 27, 2026"); time is in the tooltip. */
const LIST_DATE_FORMATTER = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' });
function fmtListDate(iso: string | null | undefined): string {
  if (!iso) {return '—';}
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : LIST_DATE_FORMATTER.format(d);
}
function fmtCardDateFull(iso: string | null | undefined): string {
  if (!iso) {return '';}
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
}

function PencilIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4z" />
    </svg>
  );
}

function GearIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

function ArchiveIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="3" width="18" height="5" rx="1" />
      <path d="M5 8v11a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8" />
      <line x1="10" y1="13" x2="14" y2="13" />
    </svg>
  );
}

function UnarchiveIcon() {
  // Box with an upward arrow — visual inverse of the archive icon.
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="3" width="18" height="5" rx="1" />
      <path d="M5 8v11a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8" />
      <polyline points="9 14 12 11 15 14" />
      <line x1="12" y1="11" x2="12" y2="18" />
    </svg>
  );
}

/** Sort direction caret shown on the active list-view column header. */
function SortCaret({ dir }: { dir: SortDir }) {
  return (
    <svg
      className="dbl-sort-caret"
      width="10" height="10" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="3"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
    >
      {dir === 'asc' ? <polyline points="6 15 12 9 18 15" /> : <polyline points="6 9 12 15 18 9" />}
    </svg>
  );
}

interface RenameFieldProps {
  value: string;
  onChange: (next: string) => void;
  onSave: (e?: React.MouseEvent | React.KeyboardEvent) => void;
  onCancel: (e?: React.MouseEvent | React.KeyboardEvent) => void;
  saving: boolean;
  error: string;
  compact?: boolean;
}

function RenameField({ value, onChange, onSave, onCancel, saving, error, compact }: RenameFieldProps) {
  return (
    <div
      className={`rename-field ${compact ? 'rename-field-compact' : ''}`}
      onClick={e => e.stopPropagation()}
    >
      <input
        className="rename-input"
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onSave(e);
          } else if (e.key === 'Escape') {
            e.preventDefault();
            onCancel(e);
          }
        }}
        autoFocus
        disabled={saving}
        aria-label="New name"
      />
      <button
        type="button"
        className="rename-btn rename-btn-save"
        onClick={onSave}
        disabled={saving || !value.trim()}
        title="Save"
      >
        {saving ? '…' : 'Save'}
      </button>
      <button
        type="button"
        className="rename-btn rename-btn-cancel"
        onClick={onCancel}
        disabled={saving}
        title="Cancel"
      >
        Cancel
      </button>
      {error && <div className="rename-error">{error}</div>}
    </div>
  );
}

// Skeleton placeholder shown in stat cells while the deferred /stats
// request is in flight. CSS class `stat-skeleton` provides the pulse
// animation (defined in index.css). Used in both grid and list views so
// the page-load endpoint can return instantly without waiting on the
// aggregation.
function StatSkeleton({ width = '60%' }: { width?: string }) {
  return (
    <span
      className="stat-skeleton"
      style={{ display: 'inline-block', width, height: '1em', verticalAlign: 'middle' }}
      aria-label="loading"
    />
  );
}

/** Source chip. Bodies predating migration 030 have no source. */
function SourceChip({ name }: { name: string | null | undefined }) {
  if (!name) {return <span className="source-chip source-chip-unset">No source</span>;}
  return <span className="source-chip" title={name}>{name}</span>;
}

function Dashboard() {
  const navigate = useNavigate();
  const [dataBodies, setDataBodies] = useState<DataBody[]>([]);
  // Stats are loaded separately from the data-body metadata so the
  // page renders immediately. While `statsLoading` is true, stat cells
  // show <StatSkeleton/> placeholders.
  //
  // 2026-09-14: the dashboard's volume columns moved off `statsToday`
  // (per-status lead counts) and onto `volumeById` (rollup-backed
  // brain-accepted volume + leg deliveries). The two are NOT the same
  // number: statsToday counts lead rows by pipeline status, volume counts
  // what the brain accepted — i.e. what will actually roll down the spine.
  const [volumeById, setVolumeById] = useState<Record<string, BodyVolume>>({});
  const [statsLoading, setStatsLoading] = useState(true);
  // Tracks whether the most recent stats fetch failed. When set, we render
  // a banner ("Stats temporarily unavailable") and keep the stat cells in
  // their skeleton state — DO NOT render zeros, which would look identical
  // to "this body has no leads". 2026-05-26 incident: dashboard rendered
  // 0/0/0/0 during Postgres recovery and looked like data loss.
  const [statsError, setStatsError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  // One piece of state for the whole create form — the fields themselves
  // live in BuildDataBodyForm, which the /new builder page renders too.
  const [buildForm, setBuildForm] = useState<BuildFormState>(emptyBuildForm);
  const [createError, setCreateError] = useState('');
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [renameSaving, setRenameSaving] = useState(false);
  const [renameError, setRenameError] = useState('');
  // Body whose gear was clicked — drives the settings modal.
  const [settingsTarget, setSettingsTarget] = useState<DataBody | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    try {
      const stored = localStorage.getItem(VIEW_MODE_KEY);
      return stored === 'list' ? 'list' : 'grid';
    } catch {
      return 'grid';
    }
  });
  const [activeTab, setActiveTab] = useState<DashboardTab>(() => {
    try {
      const stored = localStorage.getItem(ACTIVE_TAB_KEY);
      return stored === 'archived' ? 'archived' : 'active';
    } catch {
      return 'active';
    }
  });
  const [sort, setSort] = useState<SortState>(readStoredSort);
  // Archive flow: target holds the data body the user just clicked Archive on,
  // which drives the confirmation modal. Cleared on confirm/cancel/close.
  const [archiveTarget, setArchiveTarget] = useState<DataBody | null>(null);
  const [archiving, setArchiving] = useState(false);
  const [archiveError, setArchiveError] = useState('');

  useEffect(() => {
    void loadDataBodies();
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(VIEW_MODE_KEY, viewMode);
    } catch {
      /* ignore storage errors (private mode, quota, etc.) */
    }
  }, [viewMode]);

  useEffect(() => {
    try {
      localStorage.setItem(ACTIVE_TAB_KEY, activeTab);
    } catch {
      /* ignore */
    }
  }, [activeTab]);

  useEffect(() => {
    try {
      localStorage.setItem(SORT_KEY, JSON.stringify(sort));
    } catch {
      /* ignore */
    }
  }, [sort]);

  async function loadDataBodies() {
    // Two parallel fetches: metadata (instant) and stats (slow,
    // server-side aggregation). Page renders as soon as metadata
    // returns; stats trickle in and replace the skeletons.
    //
    // Stats result is wrapped in an { ok: true, data } | { ok: false, err }
    // discriminated union so we can distinguish "this body has 0 leads"
    // (render 0) from "fetch failed" (render banner + keep skeletons).
    // 2026-05-26 incident: the old `.catch(() => emptyObj)` masked a
    // Postgres-recovery outage and made every body's tiles read 0/0/0/0,
    // which looked exactly like total data loss.
    try {
      const bodiesPromise = api.getDataBodies();
      const statsPromise = api.getDataBodiesStats().then(
        data => ({ ok: true as const, data }),
        err => {
          console.warn('Failed to load dashboard stats:', err);
          const msg =
            err?.response?.data?.error ||
            err?.message ||
            'Stats unavailable';
          return { ok: false as const, err: msg };
        }
      );

      const bodies = await bodiesPromise;
      setDataBodies(bodies);
      setLoading(false);

      const stats = await statsPromise;
      if (stats.ok) {
        setVolumeById(stats.data.volume || {});
        setStatsError(null);
        setStatsLoading(false);
      } else {
        setStatsError(stats.err);
        // Intentionally NOT setting statsLoading(false) — leaves stat
        // cells in skeleton state, never zeros. User sees a banner up
        // top explaining why.
      }
    } catch (err) {
      console.error('Failed to load data bodies:', err);
      setLoading(false);
      setStatsLoading(false);
    }
  }

  async function handleCreate() {
    setCreateError('');
    try {
      const body = await createFromBuildForm(buildForm);
      setShowCreateModal(false);
      setBuildForm(emptyBuildForm());
      void navigate(`/body/${body.id}`);
    } catch (err: any) {
      const detail = err.response?.data?.detail || err.response?.data?.error || err.message || 'Unknown error';
      console.error('Failed to create data body:', detail);
      setCreateError(detail);
    }
  }

  function startRename(e: React.MouseEvent, body: DataBody) {
    e.stopPropagation();
    setRenamingId(body.id);
    setRenameDraft(body.name);
    setRenameError('');
  }

  function cancelRename(e?: React.MouseEvent | React.KeyboardEvent) {
    e?.stopPropagation();
    setRenamingId(null);
    setRenameDraft('');
    setRenameError('');
  }

  async function commitRename(e?: React.MouseEvent | React.KeyboardEvent) {
    e?.stopPropagation();
    if (!renamingId) {return;}
    const trimmed = renameDraft.trim();
    if (!trimmed) {
      setRenameError('Name cannot be empty');
      return;
    }
    const original = dataBodies.find(b => b.id === renamingId);
    if (original && original.name === trimmed) {
      cancelRename();
      return;
    }
    setRenameSaving(true);
    setRenameError('');
    try {
      const updated = await api.updateDataBody(renamingId, { name: trimmed });
      setDataBodies(prev => prev.map(b => (b.id === renamingId ? { ...b, ...updated } : b)));
      setRenamingId(null);
      setRenameDraft('');
    } catch (err: any) {
      const detail = err.response?.data?.detail || err.response?.data?.error || err.message || 'Rename failed';
      setRenameError(detail);
    } finally {
      setRenameSaving(false);
    }
  }

  // ---- Settings ----------------------------------------------------
  function openSettings(e: React.MouseEvent, body: DataBody) {
    e.stopPropagation();
    setSettingsTarget(body);
  }

  // The PATCH response omits sourceName (only the LIST query selects it),
  // so merge onto the existing row rather than replacing it — otherwise
  // saving settings would blank out the Source column until the next reload.
  function handleSettingsSaved(updated: DataBody) {
    setDataBodies(prev => prev.map(b => (b.id === updated.id ? { ...b, ...updated } : b)));
  }

  // ---- Archive / unarchive -----------------------------------------
  // Archiving requires confirmation because it stops the webhook from
  // accepting new leads. Unarchiving is reversible and harmless, so we
  // do it inline without a modal.

  function startArchive(e: React.MouseEvent, body: DataBody) {
    e.stopPropagation();
    setArchiveTarget(body);
    setArchiveError('');
  }

  function cancelArchive() {
    if (archiving) {return;}
    setArchiveTarget(null);
    setArchiveError('');
  }

  async function confirmArchive() {
    if (!archiveTarget) {return;}
    setArchiving(true);
    setArchiveError('');
    try {
      const updated = await api.updateDataBody(archiveTarget.id, { status: 'archived' });
      setDataBodies(prev => prev.map(b => (b.id === archiveTarget.id ? { ...b, ...updated } : b)));
      setArchiveTarget(null);
    } catch (err: any) {
      const detail = err.response?.data?.detail || err.response?.data?.error || err.message || 'Archive failed';
      setArchiveError(detail);
    } finally {
      setArchiving(false);
    }
  }

  async function handleUnarchive(e: React.MouseEvent, body: DataBody) {
    e.stopPropagation();
    try {
      const updated = await api.updateDataBody(body.id, { status: 'active' });
      setDataBodies(prev => prev.map(b => (b.id === body.id ? { ...b, ...updated } : b)));
    } catch (err: any) {
      const detail = err.response?.data?.detail || err.response?.data?.error || err.message || 'Unarchive failed';
      console.error('Failed to unarchive:', detail);
      window.alert(`Couldn't unarchive: ${detail}`);
    }
  }

  // ---- Sorting -----------------------------------------------------
  // Clicking the active column flips direction; clicking a new column
  // adopts that column's natural direction (numbers biggest-first, text
  // A–Z) rather than inheriting the previous column's, which is what makes
  // a sort feel "wrong" when you switch from Name to Today.
  function toggleSort(key: SortKey) {
    setSort(prev =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: defaultDirFor(key) }
    );
  }

  // Split bodies by status for the tab views. Anything not explicitly
  // archived is treated as "active" (covers active + paused).
  const archivedBodies = useMemo(() => dataBodies.filter(b => b.status === 'archived'), [dataBodies]);
  const activeBodies = useMemo(() => dataBodies.filter(b => b.status !== 'archived'), [dataBodies]);

  const visibleBodies = useMemo(() => {
    const base = activeTab === 'archived' ? archivedBodies : activeBodies;

    // Bodies whose volume hasn't loaded (or failed to load) sort LAST in
    // every numeric direction. Treating "unknown" as 0 would park them at
    // the top of an ascending sort and look like a fleet of dead bodies.
    const numericValue = (b: DataBody, key: SortKey): number | null => {
      // Timestamps come with the body itself, not the volume fetch, so
      // they're resolved before the "volume not loaded" bail-out below.
      if (key === 'createdAt' || key === 'updatedAt') {
        const t = Date.parse(b[key]);
        return Number.isNaN(t) ? null : t;
      }
      const v = volumeById[b.id];
      if (!v) {return null;}
      switch (key) {
        case 'today': return v.today;
        case 'sevenDay': return v.sevenDay;
        case 'gross': return v.gross;
        case 'deliveryRate': return v.deliveryRate;
        default: return null;
      }
    };

    const textValue = (b: DataBody, key: SortKey): string => {
      switch (key) {
        case 'source': return (b.sourceName || '').toLowerCase();
        case 'name': return b.name.toLowerCase();
        case 'status': return b.status;
        default: return '';
      }
    };

    const col = COLUMNS.find(c => c.key === sort.key);
    const mult = sort.dir === 'asc' ? 1 : -1;

    // Copy before sorting — Array.prototype.sort mutates, and `base` is
    // derived from state.
    return [...base].sort((a, b) => {
      let cmp = 0;
      if (col?.numeric) {
        const av = numericValue(a, sort.key);
        const bv = numericValue(b, sort.key);
        if (av == null && bv == null) {cmp = 0;}
        else if (av == null) {return 1;}   // unknowns last, regardless of dir
        else if (bv == null) {return -1;}
        else {cmp = av - bv;}
      } else {
        cmp = textValue(a, sort.key).localeCompare(textValue(b, sort.key));
      }
      // Name is the tiebreaker so equal values (very common: a fleet of
      // bodies all at 0 today) land in a stable, readable order instead of
      // shuffling between renders.
      if (cmp === 0) {return a.name.localeCompare(b.name);}
      return cmp * mult;
    });
  }, [activeTab, activeBodies, archivedBodies, volumeById, sort]);

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading...</div>;
  }

  /** One volume figure, skeleton-aware. Shared by both views. */
  const volumeCellContent = (body: DataBody, field: 'today' | 'sevenDay' | 'gross') => {
    const v = volumeById[body.id];
    if (statsLoading) {return <StatSkeleton width="50%" />;}
    return fmtStat(v ? v[field] : 0);
  };

  const rateFor = (body: DataBody): number | null => volumeById[body.id]?.deliveryRate ?? null;

  const rateTitle = (body: DataBody): string => {
    const v = volumeById[body.id];
    if (!v) {return 'Delivery rate: loading…';}
    if (v.today === 0) {return 'No volume accepted today, so there is no delivery rate yet.';}
    return (
      `${fmtStatExact(v.deliveriesToday)} leg deliveries / ` +
      `${fmtStatExact(v.today)} accepted today. ` +
      `Over 100% is normal — leads delivered to more than one destination count once per destination.`
    );
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">Manage your lead processing data bodies</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="16"/>
            <line x1="8" y1="12" x2="16" y2="12"/>
          </svg>
          Build Data Body
        </button>
      </div>

      {/* Stats-fetch failure banner. Rendered above the body cards so the
          user immediately understands the stat tiles are not showing zeros
          because of data loss, but because the stats endpoint is down. */}
      {statsError && (
        <div
          role="alert"
          style={{
            margin: '0 0 16px 0',
            padding: '12px 16px',
            border: '1px solid #f5c2c7',
            background: '#f8d7da',
            color: '#842029',
            borderRadius: 6,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <div>
            <strong>Dashboard stats temporarily unavailable.</strong>{' '}
            Your lead data is safe — only the count display failed.{' '}
            <span style={{ opacity: 0.8, fontSize: '0.9em' }}>
              ({statsError})
            </span>
          </div>
          <button
            className="btn btn-secondary"
            onClick={() => {
              setStatsLoading(true);
              setStatsError(null);
              void loadDataBodies();
            }}
          >
            Retry
          </button>
        </div>
      )}

      {dataBodies.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="5" r="3"/>
              <line x1="12" y1="8" x2="12" y2="16"/>
              <line x1="12" y1="16" x2="8" y2="22"/>
              <line x1="12" y1="16" x2="16" y2="22"/>
            </svg>
          </div>
          <h2 className="empty-state-title">No Data Bodies Yet</h2>
          <p className="empty-state-desc">
            Create your first data body to start processing leads. Set up a brain for inputs,
            add vertebrae for validation and enrichment, then configure legs for routing.
          </p>
          <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
            Build Your First Data Body
          </button>
        </div>
      ) : (
        <>
          <div className="dashboard-toolbar">
            <div className="dashboard-tabs" role="tablist" aria-label="Data body status">
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'active'}
                className={`dashboard-tab ${activeTab === 'active' ? 'active' : ''}`}
                onClick={() => setActiveTab('active')}
              >
                Active <span className="dashboard-tab-count">{activeBodies.length}</span>
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'archived'}
                className={`dashboard-tab ${activeTab === 'archived' ? 'active' : ''}`}
                onClick={() => setActiveTab('archived')}
              >
                Archived <span className="dashboard-tab-count">{archivedBodies.length}</span>
              </button>
            </div>

            <div className="dashboard-toolbar-right">
              {/* Sort control. Always rendered — in list view the headers are
                  the primary control, but keeping this here means the current
                  sort is stated in words in both views rather than only being
                  inferable from a caret. */}
              <div className="sort-control">
                <label className="sort-label" htmlFor="dashboard-sort">Sort</label>
                <select
                  id="dashboard-sort"
                  className="sort-select"
                  value={sort.key}
                  onChange={e => setSort({ key: e.target.value as SortKey, dir: defaultDirFor(e.target.value as SortKey) })}
                >
                  {COLUMNS.map(c => (
                    <option key={c.key} value={c.key}>{c.label}</option>
                  ))}
                </select>
                <button
                  type="button"
                  className="sort-dir-btn"
                  onClick={() => setSort(p => ({ ...p, dir: p.dir === 'asc' ? 'desc' : 'asc' }))}
                  title={sort.dir === 'asc' ? 'Ascending — click for descending' : 'Descending — click for ascending'}
                  aria-label={`Sort direction: ${sort.dir === 'asc' ? 'ascending' : 'descending'}`}
                >
                  <SortCaret dir={sort.dir} />
                </button>
              </div>

              <div className="view-toggle" role="group" aria-label="View mode">
                <button
                  type="button"
                  className={`view-toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
                  onClick={() => setViewMode('grid')}
                  aria-pressed={viewMode === 'grid'}
                  title="Grid view"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="7" height="7" />
                    <rect x="14" y="3" width="7" height="7" />
                    <rect x="3" y="14" width="7" height="7" />
                    <rect x="14" y="14" width="7" height="7" />
                  </svg>
                  <span>Grid</span>
                </button>
                <button
                  type="button"
                  className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
                  onClick={() => setViewMode('list')}
                  aria-pressed={viewMode === 'list'}
                  title="List view"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="8" y1="6" x2="21" y2="6" />
                    <line x1="8" y1="12" x2="21" y2="12" />
                    <line x1="8" y1="18" x2="21" y2="18" />
                    <line x1="3" y1="6" x2="3.01" y2="6" />
                    <line x1="3" y1="12" x2="3.01" y2="12" />
                    <line x1="3" y1="18" x2="3.01" y2="18" />
                  </svg>
                  <span>List</span>
                </button>
              </div>
            </div>
          </div>

          {visibleBodies.length === 0 ? (
            <div className="empty-state empty-state-tab">
              <h2 className="empty-state-title">
                {activeTab === 'archived' ? 'No archived data bodies' : 'No active data bodies'}
              </h2>
              <p className="empty-state-desc">
                {activeTab === 'archived'
                  ? 'Archived data bodies will appear here. Archiving stops the webhook from receiving new leads but preserves all existing data.'
                  : 'All your data bodies are currently archived. Switch to the Archived tab to view or restore them.'}
              </p>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="card-grid">
              {visibleBodies.map(body => (
                <div
                  key={body.id}
                  className={`card ${body.status === 'archived' ? 'card-archived' : ''}`}
                  onClick={() => {
                    if (renamingId === body.id) {return;}
                    void navigate(`/body/${body.id}`);
                  }}
                >
                  <div className="card-header">
                    {renamingId === body.id ? (
                      <RenameField
                        value={renameDraft}
                        onChange={setRenameDraft}
                        onSave={commitRename}
                        onCancel={cancelRename}
                        saving={renameSaving}
                        error={renameError}
                      />
                    ) : (
                      <div className="card-title-row">
                        {/* Same hierarchy as the list: name leads, source sits
                            beside it as its attribution. */}
                        <h3 className="card-title">{body.name}</h3>
                        <SourceChip name={body.sourceName} />
                        <button
                          type="button"
                          className="rename-trigger"
                          title="Rename"
                          aria-label={`Rename ${body.name}`}
                          onClick={e => startRename(e, body)}
                        >
                          <PencilIcon />
                        </button>
                        {body.status === 'archived' ? (
                          <button
                            type="button"
                            className="rename-trigger archive-trigger"
                            title="Restore from archive"
                            aria-label={`Unarchive ${body.name}`}
                            onClick={e => handleUnarchive(e, body)}
                          >
                            <UnarchiveIcon />
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="rename-trigger archive-trigger"
                            title="Archive"
                            aria-label={`Archive ${body.name}`}
                            onClick={e => startArchive(e, body)}
                          >
                            <ArchiveIcon />
                          </button>
                        )}
                      </div>
                    )}
                    <div className="card-header-right">
                      <span className={`card-status ${body.status}`}>{body.status}</span>
                      {/* Retention / rate / concurrency used to be printed
                          here as a line of grey text. They live behind this
                          gear now — same settings, still editable, no longer
                          taking up a row on a screen that's about volume. */}
                      <button
                        type="button"
                        className="settings-trigger"
                        title="Settings — retention, rate, concurrency"
                        aria-label={`Settings for ${body.name}`}
                        onClick={e => openSettings(e, body)}
                      >
                        <GearIcon />
                      </button>
                    </div>
                  </div>

                  <div className="card-stats">
                    <div className="stat" title={`Accepted today: ${fmtStatExact(volumeById[body.id]?.today)}`}>
                      <div className="stat-value">{volumeCellContent(body, 'today')}</div>
                      <div className="stat-label">Today</div>
                    </div>
                    <div className="stat" title={`Accepted over 7 days: ${fmtStatExact(volumeById[body.id]?.sevenDay)}`}>
                      <div className="stat-value">{volumeCellContent(body, 'sevenDay')}</div>
                      <div className="stat-label">7 Days</div>
                    </div>
                    <div className="stat" title={`Accepted all time: ${fmtStatExact(volumeById[body.id]?.gross)}`}>
                      <div className="stat-value">{volumeCellContent(body, 'gross')}</div>
                      <div className="stat-label">Gross</div>
                    </div>
                    <div className="stat" title={rateTitle(body)}>
                      <div className={`stat-value ${rateClassName(rateFor(body))}`}>
                        {statsLoading ? <StatSkeleton width="55%" /> : fmtRate(rateFor(body))}
                      </div>
                      <div className="stat-label">Delivery Rate</div>
                    </div>
                  </div>

                  <div className="card-dates">
                    <span title={fmtCardDateFull(body.createdAt)}>
                      Created {fmtCardDate(body.createdAt)}
                    </span>
                    <span title={fmtCardDateFull(body.updatedAt)}>
                      Updated {fmtCardDate(body.updatedAt)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="data-body-list" role="table" aria-label="Data bodies">
              <div className="data-body-list-header" role="row">
                {COLUMNS.map(col => {
                  const active = sort.key === col.key;
                  return (
                    <div
                      key={col.key}
                      className={`dbl-col dbl-col-${col.key} ${col.numeric ? 'dbl-col-stat' : ''}`}
                      role="columnheader"
                      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                    >
                      <button
                        type="button"
                        className={`dbl-sort-btn ${active ? 'active' : ''}`}
                        onClick={() => toggleSort(col.key)}
                        title={`Sort by ${col.label}`}
                      >
                        <span>{col.label}</span>
                        {active && <SortCaret dir={sort.dir} />}
                      </button>
                    </div>
                  );
                })}
                {/* Gear column — no header text, it's an affordance not a field. */}
                <div className="dbl-col dbl-col-actions" role="columnheader">
                  <span className="visually-hidden">Settings</span>
                </div>
              </div>

              {visibleBodies.map(body => (
                <div
                  key={body.id}
                  className={`data-body-list-row ${body.status === 'archived' ? 'row-archived' : ''}`}
                  role="row"
                  tabIndex={renamingId === body.id ? -1 : 0}
                  onClick={() => {
                    if (renamingId === body.id) {return;}
                    void navigate(`/body/${body.id}`);
                  }}
                  onKeyDown={e => {
                    if (renamingId === body.id) {return;}
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      void navigate(`/body/${body.id}`);
                    }
                  }}
                >
                  <div className="dbl-col dbl-col-source" role="cell">
                    <SourceChip name={body.sourceName} />
                  </div>

                  <div className="dbl-col dbl-col-name" role="cell">
                    {renamingId === body.id ? (
                      <RenameField
                        value={renameDraft}
                        onChange={setRenameDraft}
                        onSave={commitRename}
                        onCancel={cancelRename}
                        saving={renameSaving}
                        error={renameError}
                        compact
                      />
                    ) : (
                      <div className="dbl-name-row">
                        <span className="dbl-name-text" title={body.name}>{body.name}</span>
                        <button
                          type="button"
                          className="rename-trigger"
                          title="Rename"
                          aria-label={`Rename ${body.name}`}
                          onClick={e => startRename(e, body)}
                        >
                          <PencilIcon />
                        </button>
                        {body.status === 'archived' ? (
                          <button
                            type="button"
                            className="rename-trigger archive-trigger"
                            title="Restore from archive"
                            aria-label={`Unarchive ${body.name}`}
                            onClick={e => handleUnarchive(e, body)}
                          >
                            <UnarchiveIcon />
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="rename-trigger archive-trigger"
                            title="Archive"
                            aria-label={`Archive ${body.name}`}
                            onClick={e => startArchive(e, body)}
                          >
                            <ArchiveIcon />
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="dbl-col dbl-col-status" role="cell">
                    <span className={`card-status ${body.status}`}>{body.status}</span>
                  </div>

                  {/* The dbl-col-<key> classes here MUST match the ones the
                      header renders — the responsive rules in index.css hide
                      a column by that class, and a cell without it would stay
                      visible and shear the row out of alignment with the
                      header. */}
                  <div
                    className="dbl-col dbl-col-today dbl-col-stat"
                    role="cell"
                    title={`Accepted today: ${fmtStatExact(volumeById[body.id]?.today)}`}
                  >
                    {volumeCellContent(body, 'today')}
                  </div>
                  <div
                    className="dbl-col dbl-col-sevenDay dbl-col-stat"
                    role="cell"
                    title={`Accepted over 7 days: ${fmtStatExact(volumeById[body.id]?.sevenDay)}`}
                  >
                    {volumeCellContent(body, 'sevenDay')}
                  </div>
                  <div
                    className="dbl-col dbl-col-gross dbl-col-stat"
                    role="cell"
                    title={`Accepted all time: ${fmtStatExact(volumeById[body.id]?.gross)}`}
                  >
                    {volumeCellContent(body, 'gross')}
                  </div>
                  <div
                    className={`dbl-col dbl-col-deliveryRate dbl-col-stat ${rateClassName(rateFor(body))}`}
                    role="cell"
                    title={rateTitle(body)}
                  >
                    {statsLoading ? <StatSkeleton width="50%" /> : fmtRate(rateFor(body))}
                  </div>
                  <div
                    className="dbl-col dbl-col-createdAt dbl-col-stat dbl-col-date"
                    role="cell"
                    title={fmtCardDateFull(body.createdAt)}
                  >
                    {fmtListDate(body.createdAt)}
                  </div>
                  <div
                    className="dbl-col dbl-col-updatedAt dbl-col-stat dbl-col-date"
                    role="cell"
                    title={fmtCardDateFull(body.updatedAt)}
                  >
                    {fmtListDate(body.updatedAt)}
                  </div>

                  <div className="dbl-col dbl-col-actions" role="cell">
                    <button
                      type="button"
                      className="settings-trigger"
                      title="Settings — retention, rate, concurrency"
                      aria-label={`Settings for ${body.name}`}
                      onClick={e => openSettings(e, body)}
                    >
                      <GearIcon />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2 className="modal-title">Build New Data Body</h2>

            <BuildDataBodyForm
              value={buildForm}
              onChange={setBuildForm}
              onSubmit={handleCreate}
              autoFocus
            />

            {createError && (
              <div style={{ padding: '8px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, color: '#dc2626', fontSize: 13, marginBottom: 12 }}>
                {createError}
              </div>
            )}

            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => { setShowCreateModal(false); setCreateError(''); }}>Cancel</button>
              <button className="btn btn-primary" onClick={handleCreate}>Create Data Body</button>
            </div>
          </div>
        </div>
      )}

      {/* Settings */}
      {settingsTarget && (
        <DataBodySettingsModal
          body={settingsTarget}
          onClose={() => setSettingsTarget(null)}
          onSaved={handleSettingsSaved}
        />
      )}

      {/* Archive confirmation */}
      {archiveTarget && (
        <div className="modal-overlay" onClick={cancelArchive}>
          <div className="modal modal-warn" onClick={e => e.stopPropagation()}>
            <h2 className="modal-title">Archive “{archiveTarget.name}”?</h2>
            <div className="modal-warn-banner" role="alert">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              <div>
                <strong>The webhook will stop accepting new leads.</strong>
                <div style={{ fontSize: 13, marginTop: 4, color: 'var(--text-dark-secondary)' }}>
                  Any source still posting to this data body's URL will get a “not active” response until you restore it.
                </div>
              </div>
            </div>
            <p style={{ fontSize: 14, color: 'var(--text-dark-secondary)', marginBottom: 16 }}>
              Existing leads, stats, brain fields, vertebrae and legs will all be preserved exactly as they are now.
              You can restore this data body from the Archived tab at any time.
            </p>
            {archiveError && (
              <div style={{ padding: '8px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, color: '#dc2626', fontSize: 13, marginBottom: 12 }}>
                {archiveError}
              </div>
            )}
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={cancelArchive} disabled={archiving}>Cancel</button>
              <button className="btn btn-danger" onClick={confirmArchive} disabled={archiving}>
                {archiving ? 'Archiving…' : 'Archive'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
